import { useEffect, type RefObject } from 'react';

const FOCUSABLE = 'a[href],button,input,select,textarea,summary,iframe,[tabindex],[contenteditable]';

/**
 * Takes a subtree out of reach of keyboard and assistive tech.
 *
 * Applied imperatively rather than as a JSX prop. React 18.3 has no typed `inert` prop
 * (React 19 added it): the `{...{ inert: '' }}` spread does reach the DOM, but the
 * type-correct-looking `inert={true}` is silently dropped with a "Received `true` for a
 * non-boolean attribute" warning — and jsdom does not implement `inert` at all, so no test
 * in this repo could catch that regression. A ref plus an effect is explicit, type-checks
 * under `@types/react@17`, and gives us somewhere to put the fallback.
 *
 * `aria-hidden` alone is not enough: focusable descendants of an `aria-hidden` subtree stay
 * tabbable, which is a real WCAG violation (Chrome logs "Blocked aria-hidden on an element
 * because its descendant retained focus").
 */
export function useInert(ref: RefObject<HTMLElement>, enabled = true): void {
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (!enabled) {
      node.removeAttribute('inert');
      return;
    }

    node.setAttribute('inert', '');

    const supportsInert = typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;
    if (supportsInert) {
      return () => node.removeAttribute('inert');
    }

    // Fallback for browsers without `inert`. A MutationObserver rather than a one-shot
    // sweep because a preview's content arrives asynchronously — at mount the panel is
    // still a skeleton and every node that matters appears later. No restore bookkeeping:
    // the subtree unmounts with the teaser.
    const sweep = () =>
      node.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((el) => {
        if (el.getAttribute('tabindex') !== '-1') el.setAttribute('tabindex', '-1');
      });

    sweep();
    const observer = new MutationObserver(sweep);
    observer.observe(node, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      node.removeAttribute('inert');
    };
  }, [ref, enabled]);
}
